import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import useAuth from "hooks/useAuth";
import useAPI from "hooks/useAPI";
import useConstants from "constants";
import FullScreenLoader from "../components/FullScreenLoader";
import useLanguageSwitcher from "hooks/useLanguageSwitcher";

const AuthMiddleware = ({ children }) => {
  const { AUTH_URL, AUTH_API_URL } = useConstants();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const location = useLocation();
  const { user, saveUser, BASE_URL, logout, cleanUpAndRedirect } = useAuth();
  const [loading, setLoading] = useState(true);
  const { api } = useAPI();

  // Helper functions to manage tokens
  const getAccessToken = () => localStorage.getItem("__at_um");
  const getRefreshToken = () => localStorage.getItem("__rt_um");
  const { switchLanguage } = useLanguageSwitcher();
  const saveTokens = (accessToken, refreshToken) => {
    localStorage.setItem("__at_um", accessToken);
    localStorage.setItem("__rt_um", refreshToken);
  };
  const clearTokens = () => {
    localStorage.removeItem("__at_um");
    localStorage.removeItem("__rt_um");
  };

  // Validate and refresh token if necessary
  const validateToken = async () => {
    const accessToken = getAccessToken();

    if (!accessToken) {
      setIsAuthenticated(false);
      return;
    }

    try {
      // Validate the access token
      await axios.post(
        `${AUTH_API_URL}/authentication/verify/`,
        { platform: "smm" },
        {
          withCredentials: true,
          headers: {
            "use-cookie-auth": "true",
          },
        }
      );
      setIsAuthenticated(true);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        // Token is invalid or expired, attempt to refresh
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          try {
            const response = await axios.post(
              `${AUTH_API_URL}/authentication/refresh/`,
              { platform: "smm" },
              {
                withCredentials: true,
                headers: {
                  "use-cookie-auth": "true",
                },
              }
            );
            saveTokens(response.data.access, response.data.refresh);
            setIsAuthenticated(true);
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            clearTokens();
            setIsAuthenticated(false);
          }
        } else {
          // No refresh token available, redirect to login
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    }
  };

  useEffect(() => {
    const validateUserAsync = async () => {
      if (isAuthenticated) {
        const accessToken = getAccessToken();
        await validateUser(accessToken);
      }
    };
    validateUserAsync();
  }, [isAuthenticated]);

  const validateUser = async (accessToken) => {
    if (!user) {
      const response = await api.get(`authentication/user/`);
      saveUser(response.data);
      if (response.data?.lang) {
        switchLanguage(response.data?.lang);
      } else {
        switchLanguage("en");
      }
    }
    setLoading(false);
  };

  const setAuthTokens = async () => {
    const accessToken = getAccessToken();

    if (!accessToken || !user) {
      try {
        const auth_response = await api.get(`get-authentication/`, {
          params: {
            platform: "smm",
          },
          useAuth: false,
        });

        if (auth_response) {
          saveTokens(auth_response.data.auth_token, auth_response.data.refresh_token);
          saveUser(auth_response.data.user_data);
          if (auth_response.data.user_data?.lang) {
            switchLanguage(auth_response.data.user_data?.lang);
          } else {
            switchLanguage("en");
          }
        }
      } catch {
        setIsAuthenticated(false);
      }
    }
  };

  // Run token validation on mount
  useEffect(() => {
    const validateAuthentication = async () => {
      await setAuthTokens();
      await validateToken();
    };
    validateAuthentication();
  }, []);

  // Handle authentication state
  if (isAuthenticated === null) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    cleanUpAndRedirect();
    // window.location.assign(`${AUTH_URL}/sign-in`);
  }

  if (loading) {
    return <FullScreenLoader />;
  }

  return children;
};

AuthMiddleware.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthMiddleware;
